import type { JSX } from "react";
import {
  createRootRoute,
  Navigate,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { useAuthContext } from "@/auth/AuthProvider";
import { ROUTES } from "@/config/constants";
import { DevTools } from "@/devtools/DevTools";
import { Header, Footer, ErrorBoundary } from "@/shared/components";
import { cn } from "@/lib/utils";

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});

function RootComponent(): JSX.Element {
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();

  if (!isAuthenticated && location.pathname !== ROUTES.LOGIN) {
    return <Navigate to={ROUTES.LOGIN} />;
  }

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-screen overflow-hidden">
        <Header />
        <div
          className={cn(
            "flex-1 overflow-auto bg-background",
            location.pathname !== ROUTES.LOGIN && "pt-4 px-[50px]",
          )}
        >
          <Outlet />
        </div>
        <Footer />
      </div>
      <DevTools />
    </ErrorBoundary>
  );
}

function RootErrorComponent({ error }: { error: Error }): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
      <h1 className="text-2xl font-semibold text-destructive">
        Navigation Error
      </h1>
      <p className="text-muted-foreground">
        {error.message || "An error occurred during navigation"}
      </p>
    </div>
  );
}
